import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'reactstrap';
import { QueryKey } from '@tanstack/react-query';
import { useUploadImageMutation } from 'http-client/hooks/useCrud';

interface ShopImageUploaderProps {
  shopId: string;
  imageType: 'logo' | 'cover';
  title: string;
  initialUrl: string;
  placeholderUrl: string;
  containerStyle?: React.CSSProperties;
  imageStyle?: React.CSSProperties;
  buttonLabel?: string;
  onUploaded?: (url: string) => void;
  uploadUrl?: string;
  invalidateQueryKey?: QueryKey;
}

const ShopImageUploader: React.FC<ShopImageUploaderProps> = ({
  shopId,
  imageType,
  title,
  initialUrl,
  placeholderUrl,
  containerStyle,
  imageStyle,
  onUploaded,
  uploadUrl,
  invalidateQueryKey,
}) => {
  const [originalUrl, setOriginalUrl] = useState<string>(initialUrl || placeholderUrl);
  const [previewUrl, setPreviewUrl] = useState<string>(initialUrl || placeholderUrl);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [temporaryPreview, setTemporaryPreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadImage, isPending: isLoading } = useUploadImageMutation(
    uploadUrl,
    invalidateQueryKey,
  );

  // Sync with parent when initialUrl changes
  useEffect(() => {
    const newUrl = initialUrl || placeholderUrl;
    setOriginalUrl(newUrl);
    setPreviewUrl(newUrl);
    setTemporaryPreview(null);
    setSelectedFile(null);
    setUploadError(null);
  }, [initialUrl, placeholderUrl]);

  // Cleanup object URL
  useEffect(() => {
    return () => {
      if (temporaryPreview) URL.revokeObjectURL(temporaryPreview);
    };
  }, [temporaryPreview]);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const openFileDialog = () => {
    resetFileInput();
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError(null);
    setSelectedFile(file);

    // Revoke previous preview if exists
    if (temporaryPreview) {
      URL.revokeObjectURL(temporaryPreview);
    }

    const preview = URL.createObjectURL(file);
    setTemporaryPreview(preview);

    resetFileInput(); // Critical for re-selecting same file
  };

  const handleUpload = () => {
    if (!selectedFile) return;

    uploadImage(
      { shopId, file: selectedFile, imageType },
      {
        onSuccess: (response) => {
          const uploadedUrl = response?.data?.url;
          if (uploadedUrl) {
            setOriginalUrl(uploadedUrl);
            setPreviewUrl(uploadedUrl);
            setTemporaryPreview(null);
            setSelectedFile(null);
            onUploaded?.(uploadedUrl);
          }
        },
        onError: (error: any) => {
          const message = error?.response?.data?.message || 'Upload failed.';
          setUploadError(message);
          handleCancel();
        },
      },
    );
  };

  const handleCancel = () => {
    if (temporaryPreview) {
      URL.revokeObjectURL(temporaryPreview);
    }

    setTemporaryPreview(null);
    setSelectedFile(null);
    setUploadError(null);
    setPreviewUrl(originalUrl);
    resetFileInput();
  };

  const currentDisplayUrl = temporaryPreview || previewUrl || placeholderUrl;
  const isInUploadMode = !!temporaryPreview;

  return (
    <div className="shop-image-uploader">
      {uploadError && <small className="text-danger d-block mb-2">{uploadError}</small>}

      <div
        className="position-relative overflow-hidden rounded bg-light"
        style={{
          width: containerStyle?.width || '100%',
          height: containerStyle?.height || 200,
          borderRadius: containerStyle?.borderRadius || 8,
          border: '1px dashed rgba(0,0,0,0.1)',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <img
          src={currentDisplayUrl}
          alt={title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            ...imageStyle,
          }}
        />

        {/* Overlay */}
        {!isLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: isInUploadMode
                ? 'rgba(0, 0, 0, 0.65)'
                : isHovered
                ? 'rgba(0, 0, 0, 0.5)'
                : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              cursor: isInUploadMode ? 'default' : 'pointer',
            }}
            onClick={!isInUploadMode ? openFileDialog : undefined}
          >
            {/* Hover Edit Mode */}
            {!isInUploadMode && isHovered && (
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  padding: '14px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                }}
              >
                <i className="bx bx-edit" style={{ fontSize: '26px' }} />
              </div>
            )}

            {/* Upload Mode - Icons */}
            {isInUploadMode && (
              <div className="d-flex gap-5">
                {/* Upload Button */}
                <div
                  onClick={handleUpload}
                  style={{
                    backgroundColor: '#198754',
                    color: 'white',
                    borderRadius: '50%',
                    width: 62,
                    height: 62,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
                    transition: 'transform 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  title="Upload Image"
                >
                  <i className="bx bx-upload" style={{ fontSize: '30px' }} />
                </div>

                {/* Cancel Button */}
                <div
                  onClick={handleCancel}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    borderRadius: '50%',
                    width: 62,
                    height: 62,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.3)',
                    transition: 'transform 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.08)')}
                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                  title="Cancel"
                >
                  <i className="bx bx-x" style={{ fontSize: '34px' }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spinner color="light" />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default ShopImageUploader;
